import { useQueryClient } from '@tanstack/react-query';
import { activityApi } from '../lib/api.js';
import type { ActivityType } from '@imza/shared';
import { useAuth } from '../lib/auth.js';

export function useActivityLog() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  /**
   * Log an activity event and invalidate relevant queries.
   * Returns immediately — does not throw (fire-and-forget).
   */
  async function logActivity(
    landlordId: number,
    type: ActivityType,
    payload?: Record<string, unknown>
  ): Promise<void> {
    try {
      await activityApi.log({ landlord_id: landlordId, type, payload });
      // Invalidate landlord activity timeline
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId, 'activity'] });
      // Invalidate KPI for current agent
      if (user) {
        queryClient.invalidateQueries({ queryKey: ['kpi', 'agent', user.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['activity'] });
    } catch {
      // Swallow — logging failure should never block the user action
    }
  }

  /**
   * Tap-to-call: logs the call then opens the tel: link.
   * Fire-and-forget pattern so the phone app opens even if log fails.
   */
  function logAndCall(landlordId: number, phone: string): void {
    // Log first — if this tab dies, the log is already en route
    logActivity(landlordId, 'call_made', { phone });
    window.location.href = `tel:${phone}`;
  }

  /**
   * Tap-to-WhatsApp: logs then opens wa.me link.
   */
  function logAndWhatsApp(
    landlordId: number,
    phone: string,
    message = "Hello, I'm calling from Imza regarding your property."
  ): void {
    logActivity(landlordId, 'whatsapp_sent', { phone });
    const normalizedPhone = phone.replace(/^\+/, '').replace(/\s/g, '');
    const url = `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return { logActivity, logAndCall, logAndWhatsApp };
}
